import { HttpClient } from '@angular/common/http';
import { firstValueFrom, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { environment } from '@env/environment';
import { PropertyRecord } from '@app/models/property.model';
import { ListingType } from '@app/listings/featured-properties/listing-type';
import { FilterUrlState, serializeFilterQueryParams } from './featured-properties-filter-url.util';

export type BrowseFields = 'map' | 'card';

export interface BrowseListingsPage {
  items: PropertyRecord[];
  total: number;
  limit?: number | null;
  offset?: number;
}

export type BrowseListingsBucket = PropertyRecord[] | BrowseListingsPage;

export type BrowseListingsPayload = Partial<Record<ListingType, BrowseListingsBucket>>;

export interface BrowseFetchOptions {
  types: readonly ListingType[];
  fields: BrowseFields;
  filterState: FilterUrlState;
  limit?: number;
  offset?: number;
  /** Always send sort for server ordering (URL may omit default descending). */
  sort?: 'ascending' | 'descending' | null;
  saleRent?: 'sale' | 'rent';
  featuredOnly?: boolean;
  /** Collapse multi-unit addresses into one list item (residential/commercial cards). */
  groupByBuilding?: boolean;
  endpoint?: 'sale' | 'rent';
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 60_000;
const DEFAULT_PAGE_SIZE = 2000;

export function buildBrowseApiQueryParams(options: BrowseFetchOptions): Record<string, string> {
  const serialized = serializeFilterQueryParams(options.filterState);
  const params: Record<string, string> = {};

  for (const [key, value] of Object.entries(serialized)) {
    // `type` is a UI category filter; API uses `types=`.
    if (key === 'type' || value == null || value === '') {
      continue;
    }
    params[key] = value;
  }

  const sort = options.sort ?? options.filterState.sort;
  if (sort === 'ascending') {
    params.sort = 'asc';
  } else {
    // Default browse order matches featured-properties (highest price first).
    params.sort = 'desc';
  }

  params.fields = options.fields;
  params.meta = '1';
  params.limit = String(options.limit ?? DEFAULT_PAGE_SIZE);
  params.offset = String(options.offset ?? 0);
  params.types = options.types.join(',');

  if (options.saleRent) {
    params.saleRent = options.saleRent;
  }
  if (options.featuredOnly) {
    params.featured = '1';
  }
  if (options.groupByBuilding) {
    params.groupBy = 'building';
  }

  return params;
}

export function browseQueryString(params: Record<string, string>): string {
  return Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

export function normalizeBrowseBucket(
  bucket: BrowseListingsBucket | undefined,
): BrowseListingsPage | null {
  if (!bucket) {
    return null;
  }

  if (Array.isArray(bucket)) {
    return { items: bucket, total: bucket.length, limit: bucket.length, offset: 0 };
  }

  return {
    items: Array.isArray(bucket.items) ? bucket.items : [],
    total: bucket.total ?? bucket.items?.length ?? 0,
    limit: bucket.limit,
    offset: bucket.offset ?? 0,
  };
}

function bulkPath(endpoint: 'sale' | 'rent'): string {
  return endpoint === 'rent' ? 'listings/rent/bulk' : 'listings/bulk';
}

/** Fetch one page of browse results for the given types. */
export async function fetchBrowseListingsPage(
  http: HttpClient,
  options: BrowseFetchOptions,
): Promise<Partial<Record<ListingType, BrowseListingsPage>>> {
  const params = buildBrowseApiQueryParams(options);
  const url = `${environment.API_URL}${bulkPath(options.endpoint ?? 'sale')}?${browseQueryString(params)}`;

  try {
    const response = await firstValueFrom(
      http.get<BrowseListingsPayload>(url).pipe(
        timeout(options.timeoutMs ?? DEFAULT_TIMEOUT_MS),
        catchError(() => of({} as BrowseListingsPayload)),
      ),
    );

    const result: Partial<Record<ListingType, BrowseListingsPage>> = {};
    for (const type of options.types) {
      const page = normalizeBrowseBucket(response?.[type]);
      if (page) {
        result[type] = page;
      }
    }
    return result;
  } catch {
    return {};
  }
}

/** Page through all matching listings for each type (card inventory / map pins). */
export async function fetchAllBrowseListings(
  http: HttpClient,
  options: Omit<BrowseFetchOptions, 'offset'> & { pageSize?: number },
): Promise<Partial<Record<ListingType, PropertyRecord[]>>> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const result: Partial<Record<ListingType, PropertyRecord[]>> = {};

  await Promise.all(
    options.types.map(async (type) => {
      const items: PropertyRecord[] = [];
      let offset = 0;
      let total: number | undefined;

      while (total === undefined || offset < total) {
        const pageResult = await fetchBrowseListingsPage(http, {
          ...options,
          types: [type],
          limit: pageSize,
          offset,
        });
        const page = pageResult[type];
        if (!page) {
          break;
        }

        items.push(...page.items);
        total = page.total;
        offset += page.items.length;

        if (page.items.length === 0) {
          break;
        }
      }

      result[type] = items;
    }),
  );

  return result;
}
